import * as apiPostSearch from 'api/erpRecipe/PostErpRecipeSearchV1';
import { Functor } from 'functions/Functor';
import * as OrderGuide from 'types/Recipe/Detail/ErpNew/OrderGuide';
import * as Product from 'types/Recipe/Detail/ErpNew/Product';
import * as ProductList from 'types/Recipe/Detail/ErpNew/ProductList';

export type Type = {
	ingredientId: string;
	segmentIndex: number;
	products: ProductList.Type;
	manuallySearchedProducts: ProductList.Type | null;
};

export function create(): Type {
	return {
		ingredientId: '',
		segmentIndex: 0,
		products: ProductList.create(),
		manuallySearchedProducts: null,
	};
}

function mergeProductArrays(obj: Type): ProductList.Type {
	return [...obj.products, ...(obj.manuallySearchedProducts ?? [])];
}

export function getMappingsAsString(obj: Type): string {
	const productNames: string[] = mergeProductArrays(obj)
		.filter((e) => e.selected)
		.map((e) => e.name);
	if (productNames.length === 0) return '';
	if (productNames.length === 1) return productNames[0];
	return `${productNames.length} Produkte`;
}

export function toggleProductSelection(obj: Type, ingredientId: string, productId: string): Type {
	if (obj.ingredientId !== ingredientId) return obj;
	if (obj.manuallySearchedProducts) {
		const newManuallySearchedProducts = obj.manuallySearchedProducts?.map((e) => {
			if (e.productId === productId) return Product.toggleSelection(e);
			return { ...e, selected: false };
		});
		return {
			...obj,
			manuallySearchedProducts: newManuallySearchedProducts ?? null,
		};
	}
	const newProducts = obj.products.map((e) => {
		if (e.productId === productId) return Product.toggleSelection(e);
		return { ...e, selected: false };
	});
	return {
		...obj,
		products: newProducts,
	};
}

export function hasSelectedProduct(obj: Type) {
	const product = obj.products.find((e) => e.selected);
	const manuallySearchedProduct = obj.manuallySearchedProducts?.find((e) => e.selected);
	return Boolean(product) || Boolean(manuallySearchedProduct);
}

export function mapFirstSuggestionToProduct(obj: Type): Type {
	if (hasSelectedProduct(obj)) return obj;
	return {
		...obj,
		products: ProductList.selectFirstProduct(obj.products),
		manuallySearchedProducts: null,
	};
}

export function getProduct(
	obj: Type,
	input: {
		ingredientId: string;
		segmentIndex: number;
		productId: string;
	}
): Product.Type | undefined {
	if (obj.segmentIndex !== input.segmentIndex) return undefined;
	if (obj.ingredientId !== input.ingredientId) return undefined;
	return ProductList.getById(
		[...obj.products, ...(obj.manuallySearchedProducts ?? [])],
		input.productId
	);
}

export function mapFromApiSearch(e: apiPostSearch.Mapping): Type {
	return {
		ingredientId: e.ingredientId,
		segmentIndex: e.segmentIndex,
		products: !e.productId
			? []
			: [
					{
						...e,
						productId: e.productId ?? '',
						sourceId: e.sourceId ?? '',
						name: e.name ?? '',
						imageUrl: e.imageUrl ?? '',
						price: Number(e.price ?? 0),
						gtin: e.gtin ?? '',
						selected: true,
						nutrients: {},
					},
			  ],
		manuallySearchedProducts: null,
	};
}

export function applyOrderGuidePriority(obj: Type, orderGuide: OrderGuide.Type): Type {
	return { ...obj, products: ProductList.applyOrderGuidePriority(obj.products, orderGuide) };
}

export function getAllProductIds(obj: Type): string[] {
	return obj.products.map((e) => e.productId.toLowerCase());
}

export function applyIngredientIdToProducts(obj: Type): Type {
	return {
		...obj,
		products: ProductList.applyIngredientIdToProducts(obj.products, obj.ingredientId),
	};
}

export function getDefaultProduct(obj: Type, ingredientId: string): Product.Type | undefined {
	return ProductList.getByIngredientId(obj.products, ingredientId);
}

export async function searchAndAddToManuallySearchedProducts(
	obj: Type,
	searchTerm: string,
	nrOfMatches: number,
	orderGuide: OrderGuide.Type
): Promise<Type> {
	const products = await Functor.From('')
		.mapAsync((_e) =>
			ProductList.getFromApiSggestions([searchTerm], {
				amountOfSuggestions: nrOfMatches,
				type: 'name',
			})
		)
		.map(ProductList.mapFromApiSuggestions)
		.map((e) => ProductList.applyIngredientIdToProducts(e, obj.ingredientId))
		.map((e) => ProductList.applyOrderGuidePriority(e, orderGuide ?? OrderGuide.create()))
		.map(ProductList.sortByPriority)
		.get();

	return {
		...obj,
		products: ProductList.deselectAll(obj.products),
		manuallySearchedProducts: products,
	};
}

export function resetManuallySearchedProducts(obj: Type): Type {
	return {
		...obj,
		manuallySearchedProducts: null,
	};
}
